import { Skeleton } from "@arcadeai/design-system/components/ui/atoms/skeleton";
import {
  McpServerPickerGrid,
  McpServerPickerHeader,
  McpServerPicker as McpServerPickerShell,
} from "@arcadeai/design-system/components/ui/organisms/mcp-server-picker";
import { cn } from "@arcadeai/design-system/lib/utils";

const SKELETON_NAME_WIDTHS = ["w-20", "w-28", "w-24", "w-32", "w-36"];
const SKELETON_CARDS = Array.from({ length: 16 }, (_, i) => ({
  key: `app-skeleton-${i}`,
  nameWidth: SKELETON_NAME_WIDTHS[i % SKELETON_NAME_WIDTHS.length],
}));

/**
 * Loading placeholder for {@link McpServerPicker}. Built on the same DS shell
 * primitives so grid breakpoints, the scroll-fade viewport, and outer layout
 * stay in lockstep with the resolved picker. Mirrors the resting state the
 * picker shows once data lands: search header, the idle (empty) selection bar,
 * and the server grid — no scope tabs, since search is idle.
 */
export function McpServerPickerSkeleton({ className }: { className?: string }) {
  return (
    <McpServerPickerShell className={cn("flex h-full flex-col", className)}>
      <McpServerPickerHeader>
        <Skeleton className="h-9 w-full rounded-lg" />
      </McpServerPickerHeader>

      {/* Selection is always empty while loading, so this matches the summary
          bar's idle placeholder rather than its filled chip row. */}
      <div className="flex min-h-9 items-center gap-2 rounded-lg bg-muted/30 px-2.5 py-1.5">
        <Skeleton className="h-5 w-32 rounded-md" />
      </div>

      <div className="scroll-fade min-h-0 flex-1 space-y-3 overflow-y-auto pr-1.5">
        <McpServerPickerGrid>
          {SKELETON_CARDS.map(({ key, nameWidth }) => (
            <div
              className="flex items-stretch rounded-lg border border-border"
              key={key}
            >
              <div className="flex min-w-0 flex-1 items-center gap-2 px-3 py-2.5">
                <Skeleton className="size-7 shrink-0 rounded-md" />
                <Skeleton className={cn("h-4 rounded", nameWidth)} />
              </div>
              <div className="flex min-w-[5.5rem] shrink-0 items-center justify-end border-border/60 border-l px-2.5">
                <Skeleton className="h-3 w-12 rounded" />
              </div>
            </div>
          ))}
        </McpServerPickerGrid>
      </div>
    </McpServerPickerShell>
  );
}
